import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Activity, ArrowLeft, ShieldCheck, Loader2, UserCog, LogIn, FolderPlus,
  Upload, Trash2, Pencil, Download, Filter
} from "lucide-react";
import { staffAdminApi } from "@/library/lib/staffApi";
import { getToken } from "@/library/lib/api";

const ACTIONS = [
  { key: "", label: "جميع الأنواع" },
  { key: "login", label: "تسجيل دخول", Icon: LogIn },
  { key: "create_folder", label: "إنشاء مجلد", Icon: FolderPlus },
  { key: "edit_folder", label: "تعديل مجلد", Icon: Pencil },
  { key: "delete_folder", label: "حذف مجلد", Icon: Trash2 },
  { key: "upload_file", label: "رفع ملف", Icon: Upload },
  { key: "edit_file", label: "تعديل ملف", Icon: Pencil },
  { key: "delete_file", label: "حذف ملف", Icon: Trash2 },
  { key: "download_file", label: "تنزيل ملف", Icon: Download },
];

const action_meta = (a) => {
  const found = ACTIONS.find(x => x.key === a);
  if (found?.Icon) return { Icon: found.Icon, label: found.label };
  if ((a || "").startsWith("public_")) return { Icon: Download, label: "تنزيل عام" };
  return { Icon: Activity, label: a };
};

const fmt_dt = (iso) => {
  try { return new Date(iso).toLocaleString("ar-SA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function StaffActivityLog() {
  const nav = useNavigate();
  const [logs, setLogs] = useState([]);
  const [action, setAction] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) { nav("/library/admin/login"); return; }
    reload();
  }, [nav]);

  const reload = async (filter = action) => {
    setLoading(true);
    try {
      const { data } = await staffAdminApi.get("/activity-log", { params: { action: filter || undefined, limit: 500 } });
      setLogs(data);
    } catch (err) {
      if (err.response?.status === 401) nav("/library/admin/login");
    } finally { setLoading(false); }
  };

  const setAndReload = (a) => { setAction(a); reload(a); };

  return (
    <div className="library-scope min-h-screen bg-[#F5F7FA]" dir="rtl">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-dark to-brand flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-brand-dark text-[15px]">سجل عمليات المنسوبين</div>
              <div className="text-[11px] text-slate-500">تتبع دخول ورفع وتعديل وحذف الملفات</div>
            </div>
          </div>
          <button onClick={() => nav("/library/admin/staff")} className="text-xs font-heading px-3 py-1.5 rounded-lg text-brand border border-brand/20 hover:bg-brand/5 flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4 rotate-180" /> إدارة الموظفين
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="text-xs font-heading text-slate-500 inline-flex items-center gap-1 me-2"><Filter className="w-3.5 h-3.5" /> تصفية:</div>
          {ACTIONS.map((a) => (
            <button
              key={a.key}
              onClick={() => setAndReload(a.key)}
              data-testid={`filter-${a.key || "all"}`}
              className={`text-xs font-heading px-3 py-1.5 rounded-lg transition-colors ${action === a.key ? "bg-brand text-white" : "bg-white text-slate-700 border border-slate-200 hover:bg-brand/5"}`}
            >
              {a.label}
            </button>
          ))}
        </div>

        <div className="premium-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="activity-log-table">
              <thead>
                <tr className="bg-brand text-white text-xs">
                  <th className="px-3 py-3 text-right w-14">#</th>
                  <th className="px-3 py-3 text-right">الوقت</th>
                  <th className="px-3 py-3 text-right">المستخدم</th>
                  <th className="px-3 py-3 text-right">النوع</th>
                  <th className="px-3 py-3 text-right">المورد</th>
                  <th className="px-3 py-3 text-right">IP</th>
                  <th className="px-3 py-3 text-right">تفاصيل</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-3 py-12 text-center text-slate-400"><Loader2 className="w-5 h-5 inline animate-spin mx-2" /> جارِ التحميل...</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-12 text-center text-slate-400">لا توجد عمليات مسجّلة.</td></tr>
                ) : logs.map((l, i) => {
                  const { Icon, label } = action_meta(l.action);
                  return (
                    <tr key={l.id} className="odd:bg-white even:bg-slate-50/60 hover:bg-brand/5">
                      <td className="px-3 py-2.5 text-slate-500 tabular-nums text-xs">{i + 1}</td>
                      <td className="px-3 py-2.5 text-slate-700 text-xs tabular-nums" dir="ltr">{fmt_dt(l.timestamp)}</td>
                      <td className="px-3 py-2.5">
                        <div className="text-[13px] font-heading font-bold text-brand-dark">{l.user?.full_name || "زائر"}</div>
                        {l.user?.username && <div className="text-[11px] text-slate-500">@{l.user.username}</div>}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-brand/5 text-brand text-[11px] font-heading">
                          <Icon className="w-3 h-3" /> {label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">
                        {l.resource_type && <div>{l.resource_type}</div>}
                        {l.meta?.name && <div className="text-slate-700 truncate max-w-[180px]" title={l.meta.name}>{l.meta.name}</div>}
                        {l.meta?.title && <div className="text-slate-700 truncate max-w-[180px]" title={l.meta.title}>{l.meta.title}</div>}
                      </td>
                      <td className="px-3 py-2.5 text-[11px] text-slate-500 tabular-nums" dir="ltr">{l.ip || "—"}</td>
                      <td className="px-3 py-2.5 text-[11px] text-slate-500">
                        {l.meta?.size ? `${(l.meta.size / 1024).toFixed(1)} KB` : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-3 text-[11px] text-slate-500 text-center">تُعرض آخر 500 عملية.</div>
      </main>
    </div>
  );
}
